import { AppType, Provider as GadgetProvider, useGadget } from "@gadgetinc/react-shopify-app-bridge";
import { NavMenu } from "@shopify/app-bridge-react";
import { Banner, Box, Card, Layout, Page } from "@shopify/polaris";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import React, { useMemo } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";

import { SubscriptionGateway } from "./components/billing/SubscriptionGateway";
import { FullScreenAppLoader } from "./components/common/FullScreenAppLoader/FullScreenAppLoader";
import { OnboardingGateway } from "./components/onboarding/OnboardingGateway";
import { SentryUserManager } from "./components/sentry/SentryUserManager";
import { makeShop, ShopQuery } from "./components/shop/queries/shopQuery";
import { api } from "./services/api";
import { i18nLike } from "./services/i18nLike";
import { appConfig } from "./configs/app";

const en = {
  error: {
    description: "There was an error loading the shop. Please try again.",
    title: "Error",
  },
  unauthenticated: {
    title: "The application can only be accessed from your Shopify admin",
  },
};

const UnauthenticatedApp = () => {
  const t = i18nLike(en);

  return (
    <Page>
      <Layout>
        <Layout.Section>
          <Card>
            <Banner title={t("unauthenticated.title")} tone="warning" />
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

const AuthenticatedApp = () => {
  const t = i18nLike(en);

  const { data: shopData, isError, isFetching: shopFetching } = useQuery(ShopQuery());
  const location = useLocation();

  const shop = shopData ? makeShop(shopData) : null;

  const navMenu = useMemo(
    () => (
      <NavMenu key={location.key}>
        <Link rel="home" to="/">
          Home
        </Link>
        <Link to="/leaks">Coupon Leak Scanner</Link>
        {shop?.subscribedTo("blocking") && <Link to="/extensions">Extension Blocking</Link>}
        {shop?.subscribedTo("blocking") && <Link to="/duplicates">Duplicate Blocking</Link>}
        <Link to="/linkblocking">Protected Links</Link>
        <Link to="/settings">Settings</Link>
      </NavMenu>
    ),
    [location, shop],
  );

  if (isError) {
    return (
      <Page>
        <Banner title={t("error.title")} tone="critical">
          {t("error.description")}
        </Banner>
      </Page>
    );
  }

  if (shopFetching) {
    return <FullScreenAppLoader />;
  }

  return (
    <>
      {navMenu}
      <SubscriptionGateway>
        <OnboardingGateway>
          <Outlet />
          <Box padding="400"></Box>
        </OnboardingGateway>
      </SubscriptionGateway>
      {/* @ts-expect-error myshopifyDomain should always be defined */}
      <SentryUserManager shop={shop} />
    </>
  );
};

const ReactQueryDevtools = React.lazy(() =>
  import("@tanstack/react-query-devtools/build/modern/production.js").then((d) => ({
    default: d.ReactQueryDevtools,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      retry: false,
      retryOnMount: false,
    },
  },
});

const AuthenticatedAppGateway = () => {
  const { isAuthenticated, loading } = useGadget();

  if (loading) {
    return <FullScreenAppLoader />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      {isAuthenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />}
      {appConfig.environment !== "production" && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
};

export default function RootLayout() {
  return (
    <GadgetProvider api={api} shopifyApiKey={window.gadgetConfig.apiKeys.shopify} type={AppType.Embedded}>
      <AuthenticatedAppGateway />
    </GadgetProvider>
  );
}
